import { Dtos } from "@inrev/common";
import {
	useApproveBondFormForAgentIssue,
	useFetchAdminBondIssuance,
} from "@portal/api/bond-request/admin";
import {
	AdminApproveBondFormForAgentIssueFormData,
	AdminBondRequest,
} from "@portal/api/bond-request/admin/types";
import { FormField, FormFieldGroup, FormItemLabel, FormRow } from "@portal/components/form";
import { FormDropdown, FormYesNo } from "@portal/components/form/fields";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Button } from "@portal/components/ui/Button";
import { NotificationCard } from "@portal/components/ui/NotificationCard";
import { useMemo } from "react";
import { FieldPath, UseFormReturn, useForm } from "react-hook-form";
import { stripEmptyResolver, typedZodResolver } from "../../../utils/form";

type AdminApproveBondFormForAgentIssueFormProps = {
	request: AdminBondRequest;
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response;
};

export const getDefaultFormData = (
	request: AdminBondRequest,
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response,
): AdminApproveBondFormForAgentIssueFormData => ({
	suretyType: request.suretyType,
	contractSuretyType:
		request.contractSuretyType === "bid_to_final" ? "final" : request.contractSuretyType,
	bondFormType: request.contractSuretyType === "bid" ? "bid" : bondForm.bondFormType || "",
	cancellable:
		request.contractSuretyType === "bid" ? false : (bondForm.attributes.cancellable ?? ""),
	continuous: request.contractSuretyType === "bid" ? true : (bondForm.attributes.continuous ?? ""),
	renewable: false,
});

// biome-ignore lint/suspicious/noExplicitAny: constraint must accept interfaces, which Record<string, unknown> rejects
type AdminApproveBondFormForAgentIssueFormFieldsProps<TData extends Record<string, any>> = {
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	formMethods: UseFormReturn<TData, any, any>;
	fieldPaths: {
		bondFormType: FieldPath<TData>;
		cancellable: FieldPath<TData>;
		continuous: FieldPath<TData>;
	};
	request: AdminBondRequest;
};

// biome-ignore lint/suspicious/noExplicitAny: constraint must accept interfaces, which Record<string, unknown> rejects
export const AdminApproveBondFormFormFields = <TData extends Record<string, any>>({
	formMethods,
	fieldPaths,
	request,
}: AdminApproveBondFormForAgentIssueFormFieldsProps<TData>) => {
	const bondFormTypeOptions = useMemo(() => {
		if (request.contractSuretyType === "bid") {
			return [{ label: "Bid", value: "bid" }];
		}
		return [
			{ label: "Performance", value: "performance" },
			{ label: "Payment", value: "payment" },
			{ label: "Performance and Payment", value: "pnp" },
		];
	}, [request.contractSuretyType]);

	const bondFormAttributesRequired = request.contractSuretyType !== "bid";

	return (
		<div className="flex flex-col space-y-[25px]">
			<FormFieldGroup condensed>
				<FormField condensed>
					<FormItemLabel required>Bond Form Type</FormItemLabel>
					<FormDropdown
						name={fieldPaths.bondFormType}
						control={formMethods.control}
						options={bondFormTypeOptions}
						readonly={request.contractSuretyType === "bid"}
						placeholder="Select one"
						compact
					/>
				</FormField>
				<FormRow className="space-x-[50px]">
					<FormField condensed className="max-w-fit">
						<FormItemLabel required={bondFormAttributesRequired}>Continuous</FormItemLabel>
						<FormYesNo name={fieldPaths.continuous} control={formMethods.control} />
					</FormField>
					<FormField condensed className="flex-2">
						<FormItemLabel required={bondFormAttributesRequired}>Cancellable</FormItemLabel>
						<FormYesNo name={fieldPaths.cancellable} control={formMethods.control} />
					</FormField>
				</FormRow>
			</FormFieldGroup>
		</div>
	);
};

const _AdminApproveBondFormForAgentIssueForm = ({
	request,
	bondForm,
}: AdminApproveBondFormForAgentIssueFormProps) => {
	const formMethods = useForm<
		AdminApproveBondFormForAgentIssueFormData,
		// biome-ignore lint/suspicious/noExplicitAny: legacy any
		any,
		Dtos.Admin.SuretyBondForm.Approve.Request
	>({
		defaultValues: getDefaultFormData(request, bondForm),
		reValidateMode: "onBlur",
		resolver: stripEmptyResolver(
			typedZodResolver(Dtos.Admin.SuretyBondForm.Approve.Request.schema),
		),
	});

	const { approveBondFormForAgentIssue, approveBondFormForAgentIssueIsLoading } =
		useApproveBondFormForAgentIssue(request.id, request.suretyAccountId);

	const handleSubmit = (data: Dtos.Admin.SuretyBondForm.Approve.Request) => {
		approveBondFormForAgentIssue(data);
	};

	return (
		<form className="flex flex-col space-y-[25px]">
			<NotificationCard type="action" text="Approve the uploaded bond form for agent issue" />
			<AdminApproveBondFormFormFields
				formMethods={formMethods}
				fieldPaths={{
					bondFormType: "bondFormType",
					cancellable: "cancellable",
					continuous: "continuous",
				}}
				request={request}
			/>
			<ConfirmationModalClickWrapper
				shouldConfirm={true}
				message="Are you sure?"
				onConfirm={formMethods.handleSubmit(handleSubmit)}
			>
				<Button color="confirm" className="max-w-fit self-center px-[50px] !mt-[40px]" filled>
					Approve for Agent Issue
				</Button>
			</ConfirmationModalClickWrapper>
			{approveBondFormForAgentIssueIsLoading && <LoadingModal />}
		</form>
	);
};

export const AdminApproveBondFormForAgentIssueForm = ({
	request,
	bondForm,
}: AdminApproveBondFormForAgentIssueFormProps) => {
	const { bondIssuance } = useFetchAdminBondIssuance(request.suretyAccountId, request.id);

	if (bondIssuance?.pendingBondFormReview && bondForm.issuanceMethod === "agent") {
		return <_AdminApproveBondFormForAgentIssueForm request={request} bondForm={bondForm} />;
	}
	return <></>;
};
